// Logged-in home page ("/"). Same look as the login landing (AuthLanding in "home" mode):
// the login card is replaced by a welcome box with an Eicher part search and quick buttons,
// and a "desk" with live orders / price requests sits under the hero.
// Every API used here is already used elsewhere in the app; if one fails, its box just hides.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, BookOpen, LayoutGrid, Package, ShoppingCart, Bell, IndianRupee, Mail, ClipboardList, Boxes, FileText, Sparkles, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useCustomerView } from '@/hooks/useCustomerView';
import AuthLanding from './AuthLanding';
import './homeLanding.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_BRAND = 'Eicher'; // welcome search looks in Eicher parts by default
const DOC_SHARING_ADMINS = ['9811117751', '9999313175'];

const fmtDate = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (isNaN(t)) return '';
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (t.toDateString() === today.toDateString()) return 'Today, ' + t.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  if (t.toDateString() === y.toDateString()) return 'Yesterday';
  return t.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};
const chipFor = (status) => {
  const s = String(status || '').toLowerCase();
  if (['delivered', 'closed', 'completed', 'answered', 'approved', 'accepted', 'quoted', 'responded'].some(k => s.includes(k))) return 'ok';
  if (['dispatch'].some(k => s.includes(k))) return 'blue';
  if (['new', 'pending', 'placed', 'counter'].some(k => s.includes(k))) return 'new';
  return 'wait';
};
const nice = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function Welcome({ name, isAdmin, cartCount, newOrders }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const h = new Date().getHours();
  const hi = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const goCatalog = (search) => {
    try { localStorage.setItem('selectedCategory', DEFAULT_BRAND); } catch (e) {}
    navigate(`/catalog?brand=${encodeURIComponent(DEFAULT_BRAND)}${search ? `&search=${encodeURIComponent(search)}` : ''}`);
  };
  return (
    <div className="jp-welcome">
      <div className="wl-hi">{hi}</div>
      <h2>{name}</h2>
      <div className="sub">Search Eicher parts, or jump straight in.</div>
      <form className="wl-search" onSubmit={(e) => { e.preventDefault(); goCatalog(q.trim()); }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Part code, old code or OE number" aria-label="Search Eicher parts" inputMode="search" enterKeyHint="search" />
        <button type="submit" className="btn btn-amber" aria-label="Search"><Search /></button>
      </form>
      <div className="wl-grid">
        <button type="button" className="pri" onClick={() => goCatalog('')}><BookOpen />Catalog</button>
        <button type="button" onClick={() => navigate('/categories')}><LayoutGrid />Categories</button>
        <button type="button" onClick={() => navigate('/my-orders')}><Package />My orders</button>
        <button type="button" onClick={() => navigate('/cart')}><ShoppingCart />Cart{cartCount > 0 && <em>{cartCount}</em>}</button>
      </div>
      {isAdmin ? (
        <div className="wl-acc"><span>{newOrders != null ? <>Latest orders: <b>{newOrders}</b></> : 'Your desk'}</span><a href="#desk">Open your desk &rarr;</a></div>
      ) : (
        <div className="wl-acc"><span>Prices shown are <b>your rates</b></span><a href="#desk">Your account &rarr;</a></div>
      )}
    </div>
  );
}

function Desk({ isAdmin, isDocAdmin, data }) {
  const navigate = useNavigate();
  const { orders, priceReqs, docCount, orderTotal } = data;
  const go = (p) => (e) => { if (e) e.preventDefault(); navigate(p); };
  return (
    <section className="wrap jp-desk" id="desk" aria-label="Your desk">
      <div className="dk-head">
        <div><div className="eyebrow">Your desk</div><h2>{isAdmin ? 'Today at Jai Paras' : 'Your account'}</h2></div>
        <p>{isAdmin ? 'Everything waiting for you, one tap away.' : 'Your orders and price requests in one place.'}</p>
      </div>

      {isAdmin && (
        <div className="stats">
          <a className="st hot" href="/admin/orders" onClick={go('/admin/orders')}><span className="k"><Bell />Orders</span><b>{orderTotal ?? '–'}</b><small>Open order list</small></a>
          <a className="st hot" href="/admin/price-requests" onClick={go('/admin/price-requests')}><span className="k"><IndianRupee />Price requests</span><b>{priceReqs ? priceReqs.filter(r => !r.read).length : '–'}</b><small>Unread</small></a>
          {isDocAdmin && <a className="st" href="/doc-sharing" onClick={go('/doc-sharing')}><span className="k"><Mail />Documents to share</span><b>{docCount ?? '–'}</b><small>Bills &amp; LRs</small></a>}
          <a className="st" href="/task-manager" onClick={go('/task-manager')}><span className="k"><ClipboardList />Task manager</span><b className="go">Open</b><small>Assign &amp; track</small></a>
          <a className="st" href="/warehouse-ops" onClick={go('/warehouse-ops')}><span className="k"><Boxes />Warehouse</span><b className="go">Open</b><small>Stock &amp; picking</small></a>
        </div>
      )}

      <div className="dk-grid">
        {orders && (
          <div className="pn">
            <h3>{isAdmin ? 'Latest orders' : 'Your orders'}<a href={isAdmin ? '/admin/orders' : '/my-orders'} onClick={go(isAdmin ? '/admin/orders' : '/my-orders')}>See all</a></h3>
            {orders.length === 0 && <div className="row"><div className="m"><small>No orders yet.</small></div></div>}
            {orders.slice(0, 4).map((o) => (
              <div className="row" key={o.order_id}>
                <div className="m">
                  <b>{isAdmin ? (o.company_name || o.customer_name || 'Order') : `Order ${o.order_id}`}</b>
                  <small>{isAdmin ? `${o.order_id} · ` : ''}{fmtDate(o.created_at)}</small>
                </div>
                <span className={`chip ${chipFor(o.status)}`}>{nice(o.status) || 'Placed'}</span>
              </div>
            ))}
          </div>
        )}
        {priceReqs && (
          <div className="pn">
            <h3>Price requests<a href={isAdmin ? '/admin/price-requests' : '/catalog'} onClick={go(isAdmin ? '/admin/price-requests' : '/catalog')}>{isAdmin ? 'See all' : 'Catalog'}</a></h3>
            {priceReqs.length === 0 && <div className="row"><div className="m"><small>No price requests.</small></div></div>}
            {priceReqs.slice(0, 4).map((r, k) => isAdmin ? (
              <div className="row" key={r.id || k}><div className="m"><b>{r.message || 'Price request'}</b><small>{fmtDate(r.created_at)}</small></div>{!r.read && <span className="chip new">New</span>}</div>
            ) : (
              <div className="row" key={r.id || k}><div className="m"><b>{r.part_name || r.jp_part_no || 'Part'}</b><small>{r.jp_part_no || ''}</small></div><span className={`chip ${chipFor(r.status)}`}>{nice(r.status)}</span></div>
            ))}
          </div>
        )}
        <div className="pn">
          <h3>Shortcuts</h3>
          <div className="shorts">
            <a href="/categories" onClick={go('/categories')}><LayoutGrid />Categories</a>
            <a href="/my-orders" onClick={go('/my-orders')}><Package />My orders</a>
            <a href="/my-quotations" onClick={go('/my-quotations')}><FileText />My quotations</a>
            <a href="/customer-quotations" onClick={go('/customer-quotations')}><Sparkles />Quotations for your customers</a>
            <a href="/cart" onClick={go('/cart')}><ShoppingCart />Cart</a>
            <a href="/profile" onClick={go('/profile')}><User />Profile</a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomeLanding() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { getTotalItems } = useCart();
  const { isCustomerView } = useCustomerView();
  const [adminRaw, setAdminRaw] = useState(false);
  const [data, setData] = useState({ orders: null, priceReqs: null, docCount: null, orderTotal: null });
  const isAdmin = adminRaw && !isCustomerView;
  const isDocAdmin = isAdmin && DOC_SHARING_ADMINS.includes(String(user?.mobile));
  const name = (user?.name || user?.business_name || 'Welcome').split(' ')[0];

  useEffect(() => {
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/admin/check`, h).then(r => setAdminRaw(!!r.data?.is_admin)).catch(() => setAdminRaw(false));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    const set = (k, v) => alive && setData(d => ({ ...d, [k]: v }));
    if (isAdmin) {
      axios.get(`${API}/admin/orders`, { ...h, params: { skip: 0, limit: 5, sort_field: 'updated_at', sort_direction: 'desc', lean: true } })
        .then(r => { set('orders', r.data?.orders || []); set('orderTotal', r.data?.total ?? null); }).catch(() => set('orders', null));
      axios.get(`${API}/price-requests/admin/notifications?limit=20`, h).then(r => set('priceReqs', r.data?.notifications || [])).catch(() => set('priceReqs', null));
      if (isDocAdmin) axios.get(`${API}/doc-sharing/pending-count`, h).then(r => set('docCount', r.data?.pending_count ?? 0)).catch(() => {});
    } else {
      axios.get(`${API}/my-orders?page=1&limit=5`, h).then(r => set('orders', r.data?.orders || [])).catch(() => set('orders', null));
      axios.get(`${API}/price-requests/my-requests`, h).then(r => set('priceReqs', r.data?.requests || [])).catch(() => set('priceReqs', null));
    }
    return () => { alive = false; };
  }, [token, isAdmin, isDocAdmin]);

  // Logged-in wording: buttons that pointed at the login box now open the Eicher catalog.
  useEffect(() => {
    const root = document.querySelector('.jpl.jpl-home');
    if (!root) return;
    const onClick = (e) => {
      const a = e.target.closest('a[href="#login"]');
      if (!a || !root.contains(a)) return;
      e.preventDefault();
      try { localStorage.setItem('selectedCategory', DEFAULT_BRAND); } catch (x) {}
      navigate(`/catalog?brand=${encodeURIComponent(DEFAULT_BRAND)}`);
    };
    root.addEventListener('click', onClick, true);
    const cta = root.querySelector('.cta-line');
    if (cta) {
      const sp = cta.querySelector('span'); if (sp) sp.textContent = 'Ready to order? Your rates and live stock are in the catalog.';
      const a = cta.querySelector('a'); if (a) a.textContent = 'Open catalog';
    }
    root.querySelectorAll('#range .sec-head p').forEach(p => { p.textContent = 'Real photos from our catalogue. Open the catalog to search by part code, old code or OE number, with live stock and your rate on every item.'; });
    return () => root.removeEventListener('click', onClick, true);
  }, [navigate]);

  return (
    <AuthLanding mode="home" afterHero={<Desk isAdmin={isAdmin} isDocAdmin={isDocAdmin} data={data} />}>
      <Welcome name={name} isAdmin={isAdmin} cartCount={getTotalItems()} newOrders={data.orderTotal} />
    </AuthLanding>
  );
}
